import React, { useEffect, useState } from "react";

import { Grid, IconButton } from "@mui/material";
import Stack from "@mui/material/Stack";
import { useTranslation } from "react-i18next";
import { FiTrash2 } from "react-icons/fi";

import { SpecialInstruction } from "../../../../models/special.instruction";
import { useAppState } from "../../../../context/StateContext";
import {
  convertBasicUnits,
  handleGetConvertValue,
  handleSaveConvertValue,
  isFtMetric
} from "../../../../util/converts";
import { DisassemblyOptions, SpecialInstructionsTypes } from "../../../../util/enums";
import { getPriceStringValid } from "../../../../util/prices";
import Input from "../../../Form/Input";
import RadioGroup from "../../../Form/RadioGroup";
import useStyles from "./styles";
import { getMeasurement, getWeightMeasurement } from "../../../../util/getMeasurement";

type DimensionAttribute = "width" | "depth" | "height";

type UpdateItemProps = {
  attribute:
    | DimensionAttribute
    | "volume"
    | "quantity"
    | "weight"
    | "comments"
    | "packer";
  value: number | string;
  additionalUpdates?: Pick<SpecialInstruction, "volume">;
};

interface FieldProps {
  label: string;
  name: string;
  ["data-cy"]?: string;
  placeholder?: string;
  value?: number;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  setOnChange: (value: string) => void;
}

const Field: React.FC<FieldProps> = (props) => {
  const { label, name, placeholder, value, leftIcon, rightIcon, setOnChange } = props;

  const [inputValue, setInputValue] = useState<string>(value ? String(value) : "");
  const [typing, setTyping] = useState<NodeJS.Timeout>();

  useEffect(() => {
    setInputValue(value ? String(value) : "");
  }, [value]);

  return (
    <Input
      type="text"
      label={label}
      name={name}
      placeholder={placeholder}
      value={inputValue}
      leftIcon={leftIcon}
      rightIcon={rightIcon}
      onChange={({ target: { value } }) => {
        if (value === "") {
          setInputValue("");

          return;
        }

        if (Number(value) >= 0) {
          const withPointer = getPriceStringValid(value);

          setInputValue(withPointer);

          if (typing) clearTimeout(typing);
          setTyping(setTimeout(() => setOnChange(withPointer), 500));
        }
      }}
    />
  );
};

const SpecialInstructionsContent = ({
  instruction,
  deleteItem,
  updateItem,
}: {
  instruction: SpecialInstruction;
  deleteItem: () => void;
  updateItem: (param: UpdateItemProps) => void;
}) => {
  const { currentUser } = useAppState();
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { convertMetersToMillimeters, convertMillimetersToMeters } = convertBasicUnits();

  const measurement = getMeasurement();
  const measurementWeight = getWeightMeasurement();
  const measurementCode = measurement?.code ?? "m3";

  const calculateCrateVolume = (widthMm: number, heightMm: number, depthMm: number) =>
    convertMillimetersToMeters(widthMm) *
    convertMillimetersToMeters(heightMm) *
    convertMillimetersToMeters(depthMm);

  const getCrateDimensionsMm = (attribute: DimensionAttribute, valueMm: number) => ({
    widthMm:
      attribute === "width"
        ? valueMm
        : convertMetersToMillimeters(instruction.width),
    heightMm:
      attribute === "height"
        ? valueMm
        : convertMetersToMillimeters(instruction.height),
    depthMm:
      attribute === "depth"
        ? valueMm
        : convertMetersToMillimeters(instruction.depth),
  });

  const getVolumeUpdate = (widthMm: number, heightMm: number, depthMm: number) => {
    if (widthMm > 0 && heightMm > 0 && depthMm > 0) {
      return { volume: calculateCrateVolume(widthMm, heightMm, depthMm) };
    }

    return {};
  };

  const handleDimensionChange = (attribute: DimensionAttribute, value: string) => {
    if (value === "") {
      updateItem({ attribute, value: "" });

      return;
    }

    const valueMm = Number(value);
    const { widthMm, heightMm, depthMm } = getCrateDimensionsMm(attribute, valueMm);

    updateItem({
      attribute,
      value: convertMillimetersToMeters(valueMm),
      additionalUpdates: getVolumeUpdate(widthMm, heightMm, depthMm),
    });
  };

  const disassemblyOptions = [
    {
      label: DisassemblyOptions.REMOVALIST,
      value: DisassemblyOptions.REMOVALIST,
    },
    {
      label: DisassemblyOptions.CUSTOMER,
      value: DisassemblyOptions.CUSTOMER,
    },
  ];

  return (
    <Grid
      className="mt-2"
      container
      direction="column"
      alignItems="center"
      justifyContent="space-between"
      spacing={1}
    >
      <Grid item container md={12} lg={12}>
        <Stack width="100%" direction="row" alignItems="center" justifyContent="space-between">
          <span className={classes.specialInstructionTitle}>{instruction?.name}</span>
          <IconButton className="mt-2" size="small" component="span" onClick={() => deleteItem()}>
            <FiTrash2 color="#F97066" fontSize="20px" />
          </IconButton>
        </Stack>
      </Grid>
      <Grid item container md={12} lg={12} justifyContent="space-between" spacing={1}>
        {instruction?.name.includes(SpecialInstructionsTypes.CRATE) && (
          <>
            <Grid item sm md={2} lg={2}>
              <Input
                name="quantity"
                label="Qty"
                placeholder="0"
                type="number"
                value={`${instruction.quantity}`}
                onKeyDown={(event) => event.key.includes(".") && event.preventDefault()}
                onChange={(event) => {
                  const isValueValid = /^[0-9]*$/;
                  const {
                    target: { value },
                  } = event;

                  if (value === "") {
                    updateItem({
                      attribute: "quantity",
                      value: "",
                    });

                    return;
                  }

                  if (value.includes(".")) return;

                  if (Number(value) >= 0 && isValueValid.test(value)) {
                    updateItem({
                      attribute: "quantity",
                      value: Number(value),
                    });
                  }
                }}
              />
            </Grid>
            <Grid item sm md={2} lg={2}>
              <Field
                name="width"
                label={`${translate("width")} (mm)`}
                placeholder="0.00"
                value={
                  instruction.width
                    ? convertMetersToMillimeters(instruction.width)
                    : undefined
                }
                setOnChange={(value) => handleDimensionChange("width", value)}
              />
            </Grid>
            <Grid item sm md={2} lg={2}>
              <Field
                name="depth"
                label={`${translate("depth")} (mm)`}
                placeholder="0.00"
                value={
                  instruction.depth
                    ? convertMetersToMillimeters(instruction.depth)
                    : undefined
                }
                setOnChange={(value) => handleDimensionChange("depth", value)}
              />
            </Grid>
            <Grid item sm md={2} lg={2}>
              <Field
                name="height"
                label={`${translate("height")} (mm)`}
                placeholder="0.00"
                value={
                  instruction.height
                    ? convertMetersToMillimeters(instruction.height)
                    : undefined
                }
                setOnChange={(value) => handleDimensionChange("height", value)}
              />
            </Grid>
            <Grid item sm md={2} lg={2}>
              <Field
                name="weight"
                label="Weight"
                placeholder={`0.00`}
                rightIcon={measurementWeight?.code ?? "kg"}
                value={instruction.weight}
                setOnChange={(value) => {
                  updateItem({
                    attribute: "weight",
                    value: value === "" ? "" : Number(value),
                  });
                }}
              />
            </Grid>
            <Grid item sm md={2} lg={2}>
              <Field
                name="volume"
                label="Volume"
                placeholder={`0.00`}
                rightIcon={measurement?.code ?? "m3"}
                value={Number(
                  handleGetConvertValue(
                    Number(instruction.volume),
                    measurementCode,
                    measurement?.decimalPlaces
                  )
                )}
                setOnChange={(value) => {
                  if (Number(value) >= 0) {
                    let volume = Number(value);

                    if (isFtMetric(measurementCode))
                      volume = Number(handleSaveConvertValue(Number(value), measurementCode));

                    updateItem({
                      attribute: "volume",
                      value: volume,
                    });
                  }
                }}
              />
            </Grid>
          </>
        )}
        {instruction?.name.includes(SpecialInstructionsTypes.DISASSEMBLY) && (
          <Grid item sm md={12} lg={12}>
            <div className="d-flex align-items-center flex-row">
              <RadioGroup
                row
                label="By:"
                aria-label="by"
                name="byCustomer"
                value={instruction.packer || ""}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  updateItem({ attribute: "packer", value: event.target.value })
                }
                options={disassemblyOptions}
              />
            </div>
          </Grid>
        )}

        <Grid item sm md={12} lg={12}>
          <Input
            name="comment"
            label="Comment"
            placeholder=""
            value={instruction.comments || ""}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              updateItem({ attribute: "comments", value: event.target.value })
            }
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default SpecialInstructionsContent;
